import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { generatePath } from 'react-router-dom';
import produce from 'immer';
import { stringify as stringifyParams } from 'hub-http/helpers/params';
import * as ModuleQueryField from '../constants/ModuleQueryField';
import { ModuleQuery, DEFAULT_MODULE_SORT_VALUE, MODULE_QUERY_FILTER_ORDER } from '../records/ModuleQuery';
import ModuleCategoryTypeUrls from '../urls/ModuleCategory';
import * as AssetSort from '../../common/constants/AssetSort';
import { getOffsetFromPageNumber, getPageNumberFromOffset, getPaginatedPathname } from '../../utils/pagination';
import { getModulesStorefrontRoute } from '../../utils/urls';
import { getAssetQueryFromLocation } from '../../utils/assetQuery';
const DEFAULT_MODULE_QUERY_LIMIT = 24;
const QUERY_STRING_DISALLOW_LIST = [ModuleQueryField.OFFERING_IDS, ModuleQueryField.LIMIT, ModuleQueryField.OFFSET];

function hasFieldValue(moduleQueryObj, field) {
  const value = moduleQueryObj[field];
  return Array.isArray(value) ? !!value.length : value != null && value !== '';
}

export function getComputedModuleQuery(parsedParams, location) {
  const {
    category,
    pageNumber
  } = parsedParams;
  const {
    search
  } = location;
  const offset = getOffsetFromPageNumber(pageNumber, DEFAULT_MODULE_QUERY_LIMIT);
  const query = getModuleQueryFromLocation({
    search
  });
  return produce(query, draft => {
    if (category) draft.categories = [category];
    draft.offset = offset;
  });
}

function toArray(param) {
  return Array.isArray(param) ? param : [param];
}

export function getModuleQueryFromLocation({
  search
}) {
  const filteredParams = getAssetQueryFromLocation(search, (record, key, value) => {
    switch (key) {
      case 'categories':
        record[key] = toArray(value);
        break;

      default:
        break;
    }
  });
  return new ModuleQuery(filteredParams);
}
export function getLocationFromModuleQuery(moduleQuery, currentLocation, isCollection = false) {
  const {
    pathname: currentPathname
  } = currentLocation;
  let pathPattern;
  let pathParams = {};

  if (isCollection) {
    pathPattern = currentPathname;
  } else {
    ({
      pathPattern,
      pathParams
    } = getPathnameFromModuleQuery(moduleQuery));
  }

  const pathname = generatePath(pathPattern, pathParams);
  const pageNumber = getPageNumberFromOffset(moduleQuery.offset, DEFAULT_MODULE_QUERY_LIMIT);
  const queryString = toQueryString(moduleQuery, pathParams);
  return {
    pathname: getPaginatedPathname(pathname, pageNumber),
    search: queryString ? `?${queryString}` : ''
  };
}
export function getPathnameFromModuleQuery({
  categories
}) {
  if (categories.length !== 1) {
    return {
      pathPattern: getModulesStorefrontRoute(),
      pathParams: {}
    };
  }

  const categoryValue = categories[0];
  return {
    pathPattern: getModulesStorefrontRoute('/:category'),
    pathParams: {
      category: ModuleCategoryTypeUrls[categoryValue]
    }
  };
}
export function getModuleQueryFieldUrl(moduleQueryField, value) {
  const urls = moduleQueryField === 'categories' ? ModuleCategoryTypeUrls : {};
  return value in urls ? urls[value] : null;
}
export function toQueryString(moduleQuery, pathParams) {
  const queryObj = moduleQuery;
  const modifiedQueryObj = Object.keys(queryObj).reduce((finalQueryObj, key) => {
    const value = queryObj[key];

    if (QUERY_STRING_DISALLOW_LIST.includes(key) || !hasFieldValue(queryObj, key)) {
      return finalQueryObj;
    }

    if (key === 'categories' && pathParams && pathParams.category) {
      return finalQueryObj;
    }

    return Object.assign({}, finalQueryObj, {
      [key]: value
    });
  }, {});

  if (ModuleQueryField.PRICE_RANGE in modifiedQueryObj) {
    if (!(ModuleQueryField.PLAN_TYPE in modifiedQueryObj)) {
      const {
        minCents,
        maxCents
      } = modifiedQueryObj[ModuleQueryField.PRICE_RANGE];
      modifiedQueryObj.priceRangeMin = minCents;
      modifiedQueryObj.priceRangeMax = maxCents;
    }

    delete modifiedQueryObj[ModuleQueryField.PRICE_RANGE];
  }

  const prefixedQueryObj = Object.keys(modifiedQueryObj).reduce((final, key) => Object.assign({}, final, {
    [`eco_${key}`]: modifiedQueryObj[key]
  }), {});
  return stringifyParams(prefixedQueryObj);
}
export function toFilterTags(moduleQuery) {
  const queryObj = moduleQuery.toJS();
  return MODULE_QUERY_FILTER_ORDER.reduce((tags, field) => {
    const value = queryObj[field];
    if (!value) return tags;
    const fieldTags = Array.isArray(value) ? value.map(val => ({
      name: field,
      value: val
    })) : {
      name: field,
      value
    };
    return tags.concat(fieldTags);
  }, []);
}
export function toSearchQuery(moduleQuery) {
  const query = moduleQuery.toJS();
  const isCustomRange = query.priceRange !== null;
  return Object.keys(query).reduce((finalQuery, key_) => {
    const key = key_;
    let value = query[key];

    if (key === ModuleQueryField.SORT_FIELDS && !value.length) {
      value = [DEFAULT_MODULE_SORT_VALUE];
    }

    if (Array.isArray(value)) {
      if (value.length) {
        switch (key) {
          case ModuleQueryField.SORT_FIELDS:
            {
              const [primarySortField] = value;

              if (primarySortField === AssetSort.RANKING_ASC) {
                finalQuery[ModuleQueryField.MIN_REVIEW_COUNT] = 3;
              }

              break;
            }

          case ModuleQueryField.OFFERING_IDS:
            {
              break;
            }

          default:
            {
              value = {
                clause: 'OR',
                values: value
              };
              break;
            }
        }
      } else {
        value = undefined;
      }
    }

    if (key === ModuleQueryField.PLAN_TYPE && isCustomRange) {
      value = undefined;
    }

    if (value && key === ModuleQueryField.MIN_OVERALL_RATING) {
      finalQuery[ModuleQueryField.MIN_REVIEW_COUNT] = 3;
    }

    if (value != null) {
      finalQuery[key] = value;
    }

    return finalQuery;
  }, {});
}
export function getDeepFilterEventPropertyValues(obj, prefix = '') {
  return Object.keys(obj).reduce((final, key) => {
    const value = obj[key];

    if (value != null && value !== '') {
      const updatedPrefix = `${prefix ? `${prefix}::` : ''}${key}`;

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === 'object' && !Array.isArray(item)) {
            final = final.concat(getDeepFilterEventPropertyValues(item, `${updatedPrefix}::${index}`));
          } else {
            final.push(`${updatedPrefix}::${item}`);
          }
        });
      } else if (typeof value === 'object') {
        final = final.concat(getDeepFilterEventPropertyValues(value, updatedPrefix));
      } else {
        final.push(`${updatedPrefix}::${value}`);
      }
    }

    return final;
  }, []);
}
export function toUsageEventProperties(moduleQuery) {
  const queryObj = moduleQuery;
  const {
    [ModuleQueryField.PLAN_TYPE]: paymentType,
    [ModuleQueryField.MIN_OVERALL_RATING]: minOverallRating,
    [ModuleQueryField.TAGS]: tags,
    [ModuleQueryField.SEARCH]: search,
    [ModuleQueryField.HAS_FREE_SUPPORT]: support,
    [ModuleQueryField.SORT_FIELDS]: sortField
  } = queryObj;
  const eventProperties = {
    paymentType,
    minOverallRating,
    search,
    searchLength: search ? search.length : undefined,
    sortField,
    support,
    tags
  };
  Object.keys(eventProperties).forEach(_key => {
    const key = _key;
    const values = eventProperties[key];

    if (values == null || Array.isArray(values) && !values.length) {
      delete eventProperties[key];
    }
  });

  const restQueryObj = _objectWithoutPropertiesLoose(queryObj, ["limit", "offset", "search"]);

  eventProperties.allFilters = getDeepFilterEventPropertyValues(restQueryObj);
  return eventProperties;
}